/**
 * Deep equality check for arrays, objects, and special types
 * Handles Date, RegExp, Map, Set, and circular references
 * Used to compare dependency arrays to prevent unnecessary re-renders
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns true if values are deeply equal, false otherwise
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  const seen = new WeakMap<object, object>();

  function eq(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a !== typeof b) return false;

    // Handle Date objects
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    // Handle RegExp
    if (a instanceof RegExp && b instanceof RegExp) {
      return a.source === b.source && a.flags === b.flags;
    }

    // Handle Map
    if (a instanceof Map && b instanceof Map) {
      if (a.size !== b.size) return false;
      for (const [key, val] of a) {
        if (!b.has(key) || !eq(val, b.get(key))) return false;
      }
      return true;
    }

    // Handle Set
    if (a instanceof Set && b instanceof Set) {
      if (a.size !== b.size) return false;
      for (const val of a) {
        if (!b.has(val)) return false;
      }
      return true;
    }

    // Handle Array
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!eq(a[i], b[i])) return false;
      }
      return true;
    }

    // Handle plain objects
    if (
      typeof a === 'object' &&
      typeof b === 'object' &&
      a !== null &&
      b !== null &&
      !Array.isArray(a) &&
      !Array.isArray(b)
    ) {
      // Circular reference check
      if (seen.has(a as object)) {
        return seen.get(a as object) === b;
      }
      seen.set(a as object, b as object);

      const keysA = Object.keys(a as Record<string, unknown>);
      const keysB = Object.keys(b as Record<string, unknown>);
      if (keysA.length !== keysB.length) return false;

      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (
          !eq(
            (a as Record<string, unknown>)[key],
            (b as Record<string, unknown>)[key]
          )
        ) {
          return false;
        }
      }

      return true;
    }

    return false;
  }

  return eq(a, b);
}
